import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const categories = [
  {
    icon: 'ri-home-heart-line',
    label: 'In Your Space',
    title: 'In-Home Programs',
    desc: 'Personalized, one-on-one creative development delivered to your home. Ideal for individuals who thrive in a comfortable, familiar environment with all equipment provided.',
    path: '/in-home-programs',
    cta: 'Learn More',
    accent: 'from-[#5B21B6] to-[#7C3AED]',
    badgeColor: 'bg-purple-50 text-brand-purple',
    programs: ['Flexible Scheduling', 'Equipment Provided', 'All Ages Welcome', 'Personalized Plans'],
    featured: false,
    eveningAvailable: true,
  },
  {
    icon: 'ri-calendar-event-line',
    label: 'Events and Brands',
    title: 'Off-Site Services',
    desc: 'Professional creative services for organizations, brands, and events. From live band entertainment and piano performances to media production and full stage coordination.',
    path: '/off-site-services',
    cta: 'View Services',
    accent: 'from-[#065F46] to-[#059669]',
    badgeColor: 'bg-emerald-50 text-emerald-700',
    programs: ['Live Band', 'Piano Performances', 'Photography and Video', 'Stage Production', 'Audio Visual', 'Creative Direction'],
    featured: true,
    eveningAvailable: false,
  },
  {
    icon: 'ri-mic-line',
    label: 'Professional Studio',
    title: 'Recording Studio',
    desc: 'A professional recording environment equipped for tracking, mixing, and mastering. Available for artists, creators, and productions looking for high-quality audio output.',
    path: '/recording-studio',
    cta: 'Book Studio Time',
    accent: 'from-[#0F172A] to-[#1E3A8A]',
    badgeColor: 'bg-slate-100 text-slate-700',
    programs: ['Professional Recording', 'Mixing and Mastering', 'Vocal Sessions', 'Production Support'],
    featured: false,
    eveningAvailable: true,
  },
  {
    icon: 'ri-mental-health-line',
    label: 'Inclusive Development',
    title: 'Neurodivergent Creative Development',
    desc: 'A dedicated, structured, and affirming program designed to support neurodivergent creatives. Every session is thoughtfully adapted to meet each individual where they are.',
    path: '/neurodivergent-development',
    cta: 'Learn More',
    accent: 'from-[#B45309] to-[#D97706]',
    badgeColor: 'bg-amber-50 text-amber-700',
    programs: ['Individualized Approach', 'Safe Environment', 'All Disciplines', 'Patient Instruction'],
    featured: false,
    eveningAvailable: true,
  },
];

function CategoryCard({ cat, isVisible, delay }: {
  cat: typeof categories[0]; isVisible: boolean; delay: string;
}) {
  return (
    <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} ${delay} group relative rounded-3xl overflow-hidden bg-white border-2 border-gray-100 card-lift hover:border-brand-purple/20 flex flex-col`}>
      {cat.featured && (
        <div className="absolute top-5 right-5 z-10 px-3 py-1 rounded-full bg-brand-yellow text-brand-blue-dark font-body text-[10px] font-bold uppercase tracking-wide">
          Popular
        </div>
      )}
      <div className={`bg-gradient-to-br ${cat.accent} p-8`}>
        <span className={`inline-block px-3 py-1 rounded-full ${cat.badgeColor} font-body text-[10px] font-semibold uppercase tracking-widest mb-5`}>
          {cat.label}
        </span>
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/15 text-white mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
          <i className={`${cat.icon} text-2xl`} />
        </div>
        <h3 className="font-heading text-white text-2xl font-black leading-tight">{cat.title}</h3>
      </div>

      <div className="p-8 flex flex-col flex-1">
        <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">{cat.desc}</p>

        <div className="flex flex-wrap gap-2 mb-6">
          {cat.programs.map((p) => (
            <span key={p} className="inline-block px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 font-body text-[11px] text-gray-500">
              {p}
            </span>
          ))}
        </div>

        <div className="mb-6">
          {cat.eveningAvailable ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-body text-xs font-semibold">
              <i className="ri-moon-line text-xs" />
              Evening Available · Until 9pm
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-400 font-body text-xs">
              <i className="ri-sun-line text-xs" />
              By Appointment / Event-Based
            </span>
          )}
        </div>

        <div className="mt-auto">
          <Link
            to={cat.path}
            className={`whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-xl font-body font-semibold text-sm transition-all cursor-pointer bg-gradient-to-r ${cat.accent} text-white hover:opacity-90 hover:gap-3`}
          >
            {cat.cta} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function CategoryGrid() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-28 bg-gradient-to-b from-white via-[#F0F4FF] to-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
            All Service Areas
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark mb-4`}>
            A Complete Creative <span className="gradient-text">Ecosystem</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-2xl mx-auto leading-relaxed`}>
            Whether you are just beginning, looking to grow professionally, or planning an event that demands excellence, Creative Institute has a service designed for you.
          </p>
          <div className={`scroll-hidden delay-400 ${titleVis ? 'scroll-visible' : ''} inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-amber-50 border border-amber-200`}>
            <i className="ri-moon-line text-amber-600 text-sm" />
            <span className="font-body text-amber-700 text-xs font-medium">Services marked <strong>Evening Available</strong> offer sessions until 9pm — Mon through Sat</span>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {categories.map((cat, i) => (
            <CategoryCard
              key={cat.title}
              cat={cat}
              isVisible={gridVis}
              delay={`delay-${(i % 3) * 100 + 100}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
